/**
 * Vibe search on the Stays hub: moods instead of checkboxes.
 *
 * Each vibe is a plain rule over what the property record actually lists, so
 * a chip never promises something the hotel did not publish. Camps carry no
 * amenity list; they belong to exactly one vibe, the desert, and to no other.
 */
export const STAY_VIBES = ["terrace", "pool", "hammam", "garden", "family", "desert"] as const;
export type StayVibe = (typeof STAY_VIBES)[number];

export const VIBE_ICON: Record<StayVibe, string> = {
  terrace: "ri-sun-line",
  pool: "ri-drop-line",
  hammam: "ri-fire-line",
  garden: "ri-plant-line",
  family: "ri-group-line",
  desert: "ri-moon-clear-line",
};

/**
 * The moods in the URL, `vibe=pool,hammam`. Several can be on at once
 * (owner, 2026-09-28), and they narrow together: a stay must have them all.
 * Unknown names are dropped, repeats collapse, the order is the chip order.
 */
export function readVibes(value: unknown): StayVibe[] {
  const raw = typeof value === "string" ? value.split(",") : Array.isArray(value) ? value : [];
  const wanted = new Set(raw.map((v) => String(v).trim()));
  return STAY_VIBES.filter((v) => wanted.has(v));
}

/** The URL value for a set of moods; `undefined` removes the param. */
export function vibesParam(vibes: StayVibe[]): string | undefined {
  return vibes.length ? STAY_VIBES.filter((v) => vibes.includes(v)).join(",") : undefined;
}

/** A hotel matches when it has every chosen mood. Nothing chosen matches all. */
export function stayHasVibes(amenities: { key: string }[] | null | undefined, vibes: StayVibe[]): boolean {
  return vibes.every((v) => stayHasVibe(amenities, v));
}

/** Camps have one mood only, the desert: they match while nothing else is asked for. */
export function campHasVibes(vibes: StayVibe[]): boolean {
  return vibes.every((v) => v === "desert");
}

const RULES: Record<Exclude<StayVibe, "desert">, RegExp> = {
  // "sun-terrace", "roof-terrace", "rooftop-pool", "rooftop-bar"...
  terrace: /roof|terrace/,
  pool: /(^|-)pools?($|-)/,
  hammam: /hammam|(^|-)spa($|-)/,
  garden: /garden/,
  // "family-run" describes the owners, not who the hotel suits.
  family: /family(?!-run)|kids/,
};

/**
 * LiteAPI facility ids, which is what `key` carries today: the list endpoint
 * sends `{ key: "104", label: "Outdoor pool" }`, never a slug, so the
 * patterns above matched nothing and every chip but Desert nights hid itself
 * (a hotel count of 0 hides a chip). Ids rather than labels because the
 * labels come back translated (`Piscine extérieure` on /fr).
 *
 * Picked from the live list on 2026-10-06. Left out on purpose: pool toys,
 * pool covers and fences, "access to a nearby pool", and spa locker rooms,
 * none of which tell a guest the hotel has the thing.
 */
const FACILITY_IDS: Record<Exclude<StayVibe, "desert">, ReadonlySet<string>> = {
  // Terrace, Sun terrace, Rooftop pool
  terrace: new Set(["15", "118", "192"]),
  // Indoor/outdoor (+ seasonal), rooftop, infinity, with view, heated,
  // plunge, pool bar, kids' pool, swimming pool, pool sun loungers
  pool: new Set(["103", "104", "120", "122", "192", "193", "194", "195", "197", "199", "258", "301", "714"]),
  // Spa/wellness center (+ services), Hammam, Full-service spa, Turkish bath
  hammam: new Set(["54", "79", "236", "557", "627", "1102"]),
  garden: new Set(["14"]),
  // Family rooms, Kids' club, kids' play equipment, Kids' pool
  family: new Set(["28", "144", "173", "258"]),
};

export function stayHasVibe(amenities: { key: string }[] | null | undefined, vibe: StayVibe): boolean {
  if (vibe === "desert") return false;
  const rule = RULES[vibe];
  const ids = FACILITY_IDS[vibe];
  return (amenities ?? []).some((a) => ids.has(a.key) || rule.test(a.key));
}
